import React, { useState, useRef, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";
import { CustomLoader, FileIcon, SendIcon } from "../../assets/icons";
import { Api } from "../../network/api";
import BlockedOverlay from "../shared/BlockedOverlay";
import { CustomDropdown } from "../shared";
import {
  getIsBlocked, clearAllAuthCookies, getSubscriptionActive,
  saveSubscriptionActive,
  saveIsEssayAllowed,
  saveEssaysAllowed
} from "../../utils/cookies";
import { logout } from "../../redux/slice/userSlice";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";


const Upload = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [isBlocked, setIsBlocked] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extractedText, setExtractedText] = useState("");
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedMonthValue, setSelectedMonthValue] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [subjectsData, setSubjectsData] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const fileInputRef = useRef(null);
  pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;



  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get("payment") === "success") {
      refreshProfile();
    }
  }, [location.search]);



  const refreshProfile = async () => {
    try {
      const response = await Api.getProfile();
      const user = response?.data;
      console.log("Profile data:", user);

      if (user) {
        saveSubscriptionActive(user.subscription_active);
        saveIsEssayAllowed(user.is_essay_allowed);
        saveEssaysAllowed(user.essays_allowed);
      }
      setSubscriptionActive(getSubscriptionActive());
      setIsBlocked(getIsBlocked());
    } catch (error) {
      console.error("Error refreshing profile:", error);
    }
  };



  useEffect(() => {
    loadSubjects();
    const blocked = getIsBlocked();
    setSubscriptionActive(getSubscriptionActive());
    setIsBlocked(blocked);


    refreshProfile();

  }, []);

  useEffect(() => {
    const handleBlockedChange = (event) => {
      setIsBlocked(event.detail.isBlocked);
    };

    window.addEventListener("isBlockedChanged", handleBlockedChange);

    return () => {
      window.removeEventListener("isBlockedChanged", handleBlockedChange);
    };
  }, []);

  const loadSubjects = async () => {
    setLoadingSubjects(true);
    try {
      const response = await Api.getSubjects();
      // API returns array directly: [{English: {2009: {2: 31}}}, {Civil Procedure: {2019: {7: 30}}}]
      const data = response?.data || [];
      console.log("Subjects data:", data);
      
      // Extract subject names directly from original data structure
      const subjectNames = data.map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }
        const subjectName = Object.keys(item)[0];
        return subjectName;
      }).filter(name => name !== null);
      
      console.log("Subject names:", subjectNames);
      setSubjectsData(data);
      // Store subjects as simple array of objects with name for dropdown
      setSubjects(subjectNames.map(name => ({ id: name, name: name })));
    } catch (error) {
      console.error("Error loading subjects:", error);
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleSubjectChange = (subjectName) => {
    console.log("Subject changed:", subjectName);
    setSelectedSubject(subjectName);
    setSelectedYear("");
    setSelectedMonth("");
    setSelectedMonthValue("");
    setAvailableYears([]);
    setAvailableMonths([]);
    
    if (subjectName && subjectsData && subjectsData.length > 0) {
      // Find the subject in original data structure
      const subjectItem = subjectsData.find(item => item && item[subjectName]);
      
      if (subjectItem && subjectItem[subjectName]) {
        const subjectData = subjectItem[subjectName];
        console.log("Subject data for", subjectName, ":", subjectData);
        
        if (subjectData && typeof subjectData === 'object' && !Array.isArray(subjectData)) {
          // Extract years from the subject data
          const yearKeys = Object.keys(subjectData);
          console.log("Year keys found:", yearKeys);
          const years = yearKeys.map(year => ({
            id: year,
            name: year
          }));
          console.log("Available years array:", years);
          setAvailableYears(years);
        } else {
          console.warn("Subject data is not an object or is missing");
        }
      } else {
        console.warn("Subject not found in data:", subjectName);
      }
    }
  };

  const handleYearChange = (yearId) => {
    console.log("Year changed:", yearId);
    setSelectedYear(yearId);
    setSelectedMonth("");
    setSelectedMonthValue("");
    setAvailableMonths([]);
    
    if (yearId && selectedSubject && subjectsData && subjectsData.length > 0) {
      // Find the subject in original data structure
      const subjectItem = subjectsData.find(item => item && item[selectedSubject]);
      
      if (subjectItem && subjectItem[selectedSubject] && subjectItem[selectedSubject][yearId]) {
        const yearData = subjectItem[selectedSubject][yearId];
        console.log("Year data for", selectedSubject, yearId, ":", yearData);
        
        if (yearData && typeof yearData === 'object' && !Array.isArray(yearData)) {
          // Extract months from the year data - month keys are the IDs we need to send
          const monthKeys = Object.keys(yearData);
          console.log("Month keys found:", monthKeys);
          const months = monthKeys.map(month => ({
            id: month, // This is the month key (e.g., "2", "7") that will be sent to API
            name: getMonthName(parseInt(month))
          }));
          console.log("Available months:", months);
          setAvailableMonths(months);
        } else {
          console.warn("Year data is not an object or is missing");
        }
      } else {
        console.warn("No data found for selected year:", yearId, "in subject:", selectedSubject);
      }
    }
  };

  const handleMonthChange = (monthId) => {
    setSelectedMonth(monthId);
    
    // Extract the month value from the data structure
    // Structure: {Subject: {Year: {Month: Value}}}
    if (monthId && selectedSubject && selectedYear && subjectsData && subjectsData.length > 0) {
      const subjectItem = subjectsData.find(item => item && item[selectedSubject]);
      
      if (subjectItem && 
          subjectItem[selectedSubject] && 
          subjectItem[selectedSubject][selectedYear] &&
          subjectItem[selectedSubject][selectedYear][monthId]) {
        const monthValue = subjectItem[selectedSubject][selectedYear][monthId];
        console.log("Month key:", monthId, "Month value:", monthValue);
        setSelectedMonthValue(monthValue);
      } else {
        console.warn("Month value not found for:", selectedSubject, selectedYear, monthId);
        setSelectedMonthValue("");
      }
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[monthNumber - 1] || `Month ${monthNumber}`;
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
      setExtractedText("")
    }
  };

  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (event) => {
    handleFileSelect(event);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setExtractedText("");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setIsExtracting(true);
    setUploadProgress(0);
    setExtractedText("");

    try {
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 10;
        });
      }, 200);

      const text = await extractTextFromFile(selectedFile);
      setExtractedText(text);
      setTimeout(() => {
        clearInterval(interval);
        setIsUploading(false);
        setIsExtracting(false);
        setUploadProgress(100);
        console.log(
          "File uploaded and text extracted successfully:",
          selectedFile.name
        );
      }, 2000);
    } catch (error) {
      console.error("Error processing file:", error);
      setIsUploading(false);
      setIsExtracting(false);
      setUploadProgress(0);
      alert("Error processing file. Please try again.");
    }
  };

  const handleSubmit = async () => {
    if (!extractedText.trim()) {
      alert("No text to submit. Please upload and process a file first.");
      return;
    }

    if (!selectedSubject) {
      alert("Please select a subject for your essay.");
      return;
    }

    if (!selectedYear) {
      alert("Please select a year for your essay.");
      return;
    }

    if (!selectedMonth || !selectedMonthValue) {
      alert("Please select a month for your essay.");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("subject", selectedMonthValue); 

      const response = await Api.uploadEssay(formData);
      console.log(response);
      setShowSuccessModal(true);
      setExtractedText("");
      setSelectedFile(null);
      setSelectedSubject("");
      setSelectedYear("");
      setSelectedMonth("");
      setSelectedMonthValue("");
      setAvailableYears([]);
      setAvailableMonths([]);
    } catch (error) {
      console.error("Upload error:", error);
      console.error("Error response:", error.response);
      
      // Safely extract error message - handle both string and object responses
      let errorMessage = "";
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMessage = typeof error.message === 'string' 
          ? error.message 
          : String(error.message);
      }
      
      const statusCode = error.response?.status;
      const errorMessageLower = errorMessage.toLowerCase();
      
      if (
        statusCode === 403 ||
        (errorMessage && errorMessageLower.includes("not authenticated")) ||
        (errorMessage && errorMessageLower.includes("unauthorized"))
      ) {
        handleLogout();
        return;
      } else {
        // Show error message to user
        alert(errorMessage || "An error occurred while uploading the essay. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const extractTextFromFile = async (file) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();

    try {
      let text = "";

      switch (fileExtension) {
        case "pdf":
          text = await extractTextFromPDF(file);
          break;
        case "doc":
        case "docx":
          text = await extractTextFromWord(file);
          break;
        case "txt":
          text = await extractTextFromTXT(file);
          break;
        default:
          throw new Error("Unsupported file format");
      }

      return text;
    } catch (error) {
      console.error("Error extracting text:", error);
      throw error;
    }
  };

  const extractTextFromPDF = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async function () {
        try {
          const typedarray = new Uint8Array(this.result);
          const pdf = await pdfjsLib.getDocument(typedarray).promise;
          let fullText = "";

          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items
              .map((item) => item.str)
              .join(" ");
            fullText += pageText + "\n";
          }

          resolve(fullText);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const extractTextFromWord = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async function () {
        try {
          const arrayBuffer = this.result;
          const result = await mammoth.extractRawText({ arrayBuffer });
          resolve(result.value);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const extractTextFromTXT = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function () {
        resolve(this.result);
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const removeFile = () => {
    setSelectedFile(null);
    setExtractedText("");
    setUploadProgress(0);
    setIsUploading(false);
    setIsExtracting(false);
    setIsSubmitting(false);
    setSelectedSubject("");
    setSelectedYear("");
    setSelectedMonth("");
    setSelectedMonthValue("");
    setAvailableYears([]);
    setAvailableMonths([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleLogout = () => {
    clearAllAuthCookies();
    dispatch(logout());
    navigate("/login");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative">
        {(isBlocked || !subscriptionActive) && (
          <BlockedOverlay isBlocked={isBlocked} subscriptionActive={subscriptionActive} />
        )}

        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Upload New Essay</h1>
          <p className="text-gray-600 mt-1">
            Upload your essay file to get started with analysis
          </p>
        </div>
        <div className={`p-6 ${(isBlocked || !subscriptionActive) ? "filter blur-[0.5px]" : ""}`}>
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Select Subject, Year, and Month
            </h3>
            <div className="w-full">
              {loadingSubjects ? (
                <div className="flex items-center space-x-2 text-gray-600">
                  <CustomLoader />
                  <span>Loading subjects...</span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-4 justify-center items-end">
                  <div className="w-[300px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </label>
                    <CustomDropdown
                      options={subjects}
                      value={selectedSubject}
                      onChange={handleSubjectChange}
                      placeholder="Select a subject"
                      loading={loadingSubjects}
                    />
                  </div>
                  
                  <div className="w-52">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year
                    </label>
                    {selectedSubject ? (
                      availableYears.length > 0 ? (
                        <CustomDropdown
                          options={availableYears}
                          value={selectedYear}
                          onChange={handleYearChange}
                          placeholder="Select a year"
                        />
                      ) : (
                        <div className="px-2 py-2 text-xs text-gray-500 border border-gray-300 rounded-md bg-gray-50">
                          No years
                        </div>
                      )
                    ) : (
                      <div className="px-2 py-2 text-xs text-gray-400 border border-gray-300 rounded-md bg-gray-50">
                        Select subject
                      </div>
                    )}
                  </div>
                  
                  <div className="w-52">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Month
                    </label>
                    {selectedYear ? (
                      availableMonths.length > 0 ? (
                        <CustomDropdown
                          options={availableMonths}
                          value={selectedMonth}
                          onChange={handleMonthChange}
                          placeholder="Select a month"
                        />
                      ) : (
                        <div className="px-2 py-2 text-xs text-gray-500 border border-gray-300 rounded-md bg-gray-50">
                          No months
                        </div>
                      )
                    ) : (
                      <div className="px-2 py-2 text-xs text-gray-400 border border-gray-300 rounded-md bg-gray-50">
                        Select year
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${selectedFile
              ? "border-primary-300 bg-primary-50"
              : "border-gray-300 hover:border-primary-400 hover:bg-gray-50"
              }`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {!selectedFile ? (
              <div>
                <FileIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleFileClick}
                    className="cursor-pointer bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Choose a file
                  </button>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    accept=".doc,.docx,.pdf,.txt"
                    onChange={handleFileInputChange}
                    ref={fileInputRef}
                  />
                </div>
                <p className="mt-2 text-sm text-gray-600">or drag and drop</p>
                <p className="mt-1 text-xs text-gray-500">
                  PDF, DOC, DOCX, TXT up to 10MB
                </p>
              </div>
            ) : (
              <div>
                <svg
                  className="mx-auto h-12 w-12 text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-900">
                    {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <div className="mt-4 flex justify-center space-x-3">
                  <button
                    onClick={handleUpload}
                    disabled={isUploading || isExtracting}
                    className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading || isExtracting
                      ? "Processing..."
                      : "Process File"}
                  </button>
                  <button
                    onClick={removeFile}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>
          {(isUploading || isExtracting) && (
            <div className="mt-6">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>
                  {isExtracting ? "Extracting text..." : "Uploading..."}
                </span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {extractedText && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Extracted Text
              </h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="max-h-80 overflow-y-auto">
                  <div className="text-sm text-gray-700  font-mono leading-relaxed">
                    {extractedText}
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  {extractedText.split("\n").length} lines extracted
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !selectedSubject || !selectedYear || !selectedMonth || !selectedMonthValue}
                  className="bg-green-600 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <SendIcon className="h-4 w-4" fill="white" />
                      <span>Submit Essay for Analysis</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}
          <div className="mt-8 bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              Upload Instructions
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Supported formats: PDF, DOC, DOCX, TXT</li>
              <li>• Maximum file size: 10MB</li>
              <li>• Ensure your essay is complete and properly formatted</li>
              <li>• The analysis will begin automatically after upload</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Success!
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Your essay has been submitted successfully! It will be
                  reviewed by admin.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="px-4 py-2 bg-green-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-300"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Upload;
